import {TFunction} from 'i18next'
import moment from 'moment'

import {DayPeriod, StrengthTarget} from '../types'

import {compareDateStrings, getTodayISOInTimezone} from './dateUtils'

const MIN_VALUE = 1
const MAX_VALUE = 100

export function validateTargetValue(
  value: number | undefined,
  lowerBound: number | undefined,
  upperBound: number | undefined,
  t: TFunction
): string | true {
  if (value === undefined) return true

  if (value < MIN_VALUE || value > MAX_VALUE) {
    return t('materialsSettings.strengthTargets.validation.targetRange')
  }

  if (lowerBound !== undefined && value <= lowerBound) {
    return t('materialsSettings.strengthTargets.validation.targetMustBeGreaterThanLowerBound')
  }

  if (upperBound !== undefined && value >= upperBound) {
    return t('materialsSettings.strengthTargets.validation.targetMustBeLessThanUpperBound')
  }

  return true
}

export function validateLowerBoundValue(
  value: number | undefined,
  target: number | undefined,
  upperBound: number | undefined,
  t: TFunction
): string | true {
  if (value === undefined) return true

  if (value < MIN_VALUE || value > MAX_VALUE) {
    return t('materialsSettings.strengthTargets.validation.lowerBoundRange')
  }

  if (target !== undefined && value >= target) {
    return t('materialsSettings.strengthTargets.validation.lowerBoundMustBeLessThanTarget')
  }

  if (upperBound !== undefined && value >= upperBound) {
    return t('materialsSettings.strengthTargets.validation.lowerBoundMustBeLessThanUpperBound')
  }

  return true
}

export function validateUpperBoundValue(
  value: number | undefined,
  target: number | undefined,
  lowerBound: number | undefined,
  t: TFunction
): string | true {
  if (value === undefined) return true

  if (value < MIN_VALUE || value > MAX_VALUE) {
    return t('materialsSettings.strengthTargets.validation.upperBoundRange')
  }

  if (target !== undefined && value <= target) {
    return t('materialsSettings.strengthTargets.validation.upperBoundMustBeGreaterThanTarget')
  }

  if (lowerBound !== undefined && value <= lowerBound) {
    return t('materialsSettings.strengthTargets.validation.upperBoundMustBeGreaterThanLowerBound')
  }

  return true
}

export function hasBackendTargetsForPeriod(
  dayPeriod: DayPeriod,
  backendTargets: StrengthTarget[]
): boolean {
  return backendTargets.some((target) => target.dayPeriod === dayPeriod)
}

export function validateValidFromValue(
  value: string | number | boolean | undefined,
  isEdit: boolean,
  dayPeriod: DayPeriod,
  existingTargets: StrengthTarget[],
  backendTargets: StrengthTarget[],
  timezone: string,
  localDateToISOInTimezone: (localDate: string, timezone: string) => string,
  t: TFunction
): string | true {
  if (!value || typeof value !== 'string') {
    return t('materialsSettings.strengthTargets.validation.validFromRequired')
  }

  const valueISO = localDateToISOInTimezone(value, timezone)
  const todayISO = getTodayISOInTimezone(timezone)
  const isPastDate = compareDateStrings(valueISO, todayISO) < 0
  const hasBackendTargets = hasBackendTargetsForPeriod(dayPeriod, backendTargets)

  if (isPastDate && hasBackendTargets) {
    return t('materialsSettings.strengthTargets.validation.cannotSetPastDate')
  }

  if (!isEdit) {
    const existingTarget = existingTargets.find(
      (target) =>
        target.dayPeriod === dayPeriod && compareDateStrings(target.validFrom, valueISO) === 0
    )
    if (existingTarget) {
      return t('materialsSettings.strengthTargets.validation.targetAlreadyExistsForDate')
    }
  }

  return true
}

export function hasAtLeastOneValue(
  target: number | undefined,
  lowerBound: number | undefined,
  upperBound: number | undefined
): boolean {
  return target !== undefined || lowerBound !== undefined || upperBound !== undefined
}

export function findNextAvailableDate(
  dayPeriod: DayPeriod,
  existingTargets: StrengthTarget[],
  backendTargets: StrengthTarget[],
  tomorrowISO: string,
  isoToLocalDateInTimezone: (isoDate: string, timezone: string) => string,
  timezone: string
): string {
  const samePeriodTargets = existingTargets.filter((target) => target.dayPeriod === dayPeriod)
  const hasBackendTargets = hasBackendTargetsForPeriod(dayPeriod, backendTargets)

  if (samePeriodTargets.length === 0) {
    return isoToLocalDateInTimezone(tomorrowISO, timezone)
  }

  if (!hasBackendTargets) {
    return isoToLocalDateInTimezone(tomorrowISO, timezone)
  }

  const targetsWithoutValidTo = samePeriodTargets.filter((target) => !target.validTo)

  if (targetsWithoutValidTo.length === 0) {
    return isoToLocalDateInTimezone(tomorrowISO, timezone)
  }

  const lastTarget = targetsWithoutValidTo.reduce((latest, current) => {
    return current.validFrom > latest.validFrom ? current : latest
  })

  const nextDayISO = moment(lastTarget.validFrom).add(1, 'day').startOf('day').toISOString()

  const resultISO = compareDateStrings(nextDayISO, tomorrowISO) < 0 ? tomorrowISO : nextDayISO

  return isoToLocalDateInTimezone(resultISO, timezone)
}
